import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Zap, Target, ArrowRight, ShieldCheck } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function Landing() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  usePageMeta({
    title: "PickemSports.club — Free Top 5 Skills Challenges With Your Crew",
    description:
      "Invite-only Top 5 skills challenges for you and your friends. Pick the top 5 QBs, RBs, WRs, TEs or Defenses, talk trash in live chat, and climb the board. Always free to enter, no cash prizes — a membership from $1.99/mo unlocks the sports you want to play.",
    path: "/",
  });

  useEffect(() => {
    if (!loading && user) navigate("/lobby", { replace: true });
    // Capture referral code from URL and save to localStorage
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("pickem_referral_code", ref.toUpperCase());
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-40 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/brand-logo.png" alt="PickemSports.club" className="w-9 h-9 rounded-full flex-shrink-0" />
            <span className="font-display text-lg sm:text-2xl tracking-widest truncate">PICKEMSPORTS<span className="text-[#00FF66]">.CLUB</span></span>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a
              href="/merch"
              data-testid="header-merch-link"
              className="hidden sm:inline text-white/60 hover:text-white text-sm uppercase tracking-widest font-semibold"
            >
              Merch
            </a>
            <a
              href="/legal"
              data-testid="header-legal-link"
              className="hidden sm:inline text-white/60 hover:text-white text-sm uppercase tracking-widest font-semibold"
            >
              Legal State Plays
            </a>
            <button
              onClick={login}
              data-testid="header-login-btn"
              className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-3 sm:px-5 py-2 text-xs sm:text-sm rounded-sm transition-colors whitespace-nowrap"
            >
              <span className="sm:hidden">Sign in</span>
              <span className="hidden sm:inline">Sign in with Google</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Football action background — looping video with image fallback */}
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.unsplash.com/photo-1696542095242-53305aaa6d9c?auto=format&fit=crop&w=2000&q=60"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          data-testid="hero-video"
        >
          <source
            src="https://videos.pexels.com/video-files/8969898/8969898-uhd_2560_1440_25fps.mp4"
            type="video/mp4"
          />
          <source
            src="https://videos.pexels.com/video-files/6042636/6042636-uhd_2560_1440_25fps.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/50 via-[#0a0a0a]/75 to-[#0a0a0a]" />

        {/* Stadium floodlights — two beams raking across the field with a slow flicker */}
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden"
          aria-hidden="true"
          data-testid="hero-floodlights"
        >
          <div className="floodlight floodlight-left" />
          <div className="floodlight floodlight-right" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#00FF66]/10 to-transparent" />
        </div>

        {/* Yard lines on the turf */}
        <div
          className="absolute inset-x-0 bottom-0 h-48 pointer-events-none opacity-[0.12]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, transparent 0 78px, rgba(255,255,255,0.9) 78px 80px)",
            maskImage: "linear-gradient(to top, black, transparent)",
            WebkitMaskImage: "linear-gradient(to top, black, transparent)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-36">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#00FF66]/40 bg-[#00FF66]/10 text-[#00FF66] rounded-sm px-3 py-1 text-xs uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" />
              Live · NFL Game Days
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.9] tracking-tight mb-6">
              PICK THE TOP 5.
              <br />
              <span className="text-[#00FF66]">RUN YOUR CREW.</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-2xl leading-relaxed mb-8">
              Rank the top 5 QBs, WRs, RBs, TEs or Defenses, set your tiebreaker, then talk trash
              in the challenge chat while the real NFL box scores decide it. Every challenge is
              free to enter — bragging rights only.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={login}
                data-testid="hero-cta-btn"
                className="group bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-6 py-4 text-sm rounded-sm inline-flex items-center gap-2"
              >
                Start with Google
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <ShieldCheck size={16} className="text-[#00FF66]" />
                No entry fees · No cash prizes · Membership from $1.99/mo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-white/10">
        <div className="mb-12">
          <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">3 STEPS TO GET IN THE GAME</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Target, num: "01", title: "PICK YOUR 5", body: "Rank your top 5 players for any position — QB, WR, RB, TE, or DEF." },
            { icon: Zap, num: "02", title: "SET TIEBREAKER", body: "Predict passing TDs, rushing yards, or sacks. Ties go to the closest guess." },
            { icon: Trophy, num: "03", title: "TAKE THE CROWN", body: "Real NFL box scores settle it live. Top of the board wins the bragging rights." },
          ].map(({ icon: Icon, num, title, body }) => (
            <div
              key={num}
              className="border border-white/10 bg-[#141414] p-6 hover:border-[#00FF66]/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-10 h-10 rounded-sm bg-[#00FF66]/10 border border-[#00FF66]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#00FF66]" />
                </div>
                <span className="font-display text-3xl text-white/20">{num}</span>
              </div>
              <h3 className="font-display text-2xl mb-2 tracking-wide">{title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Levels */}
      <section
        className="relative py-20 border-t border-white/10 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.85), rgba(10,10,10,0.95)), url(https://images.unsplash.com/photo-1763449448560-ffd1d982e96d?auto=format&fit=crop&w=2000&q=60)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">Membership</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">PICK YOUR SPORTS</h2>
            <p className="text-white/50 text-base mt-3 max-w-2xl">
              Challenges are always free to enter and never pay cash. A monthly membership unlocks
              how many sports you can play, and every entry still earns XP from Rookie to Elite.
              NFL is live now — the rest land as each season opens.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {[
              { name: "Single Sport", price: "$1.99", desc: "One sport of your choice. Perfect for NFL Sundays with the group chat." },
              { name: "Triple Threat", price: "$2.99", desc: "Up to three sports. Most popular once basketball and baseball open up.", featured: true },
              { name: "All Access", price: "$8.99", desc: "Up to eight sports, every challenge your crew can dream up." },
            ].map(({ name, price, desc, featured }) => (
              <div
                key={name}
                data-testid={`landing-plan-${name.toLowerCase().replace(/\s/g, "-")}`}
                className={`border bg-black/60 backdrop-blur p-6 ${
                  featured ? "border-[#00FF66]/60" : "border-white/10"
                }`}
              >
                {featured && (
                  <div className="text-[#00FF66] text-[10px] uppercase tracking-widest mb-2">Most popular</div>
                )}
                <div className="font-display text-2xl tracking-wide">{name.toUpperCase()}</div>
                <div className="font-display text-5xl mt-3 mb-1">{price}</div>
                <div className="text-white/40 text-xs uppercase tracking-widest mb-3">Per month · cancel anytime</div>
                <p className="text-white/50 text-sm">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 border border-white/10 bg-black/60 backdrop-blur p-5 flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#00FF66] flex-shrink-0 mt-0.5" />
            <p className="text-white/50 text-sm">
              PickemSports is a skills challenge club for friends. There are no entry fees, no prize
              pools and no payouts — your membership only unlocks the sports you play.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-white/30 text-xs uppercase tracking-widest">
        <a href="/legal" className="hover:text-white/60">Legal State Plays</a>
        <span className="mx-3">·</span>
        <a href="/merch" className="hover:text-white/60">Merch</a>
        <span className="mx-3">·</span>
        <a href="/contact" className="hover:text-white/60">Contact</a>
        <span className="mx-3">·</span>
        18+ · Free to enter · No cash prizes
        <div className="mt-2 text-[10px] text-white/20 normal-case tracking-wider">
          © 2026 I3 Company · doing business as PickemSports.club · All rights reserved
        </div>
      </footer>
    </div>
  );
}
